import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable } from "@apollo/client";
import { GraphQLError } from "graphql";
import { useMemo } from "react";
import { Subscription } from "zen-observable-ts";
import { getClientSideGraphqlPath, getServerRuntimeConfig, isServerSide } from "../config";
import { ensureError } from "../core/error";
import { getLocalUniqueId } from "../core/localUniqueId";
import logger from "../core/logger";

let apolloClient: ApolloClient<any> | undefined;

function errorHandler({
  networkError,
  graphQLErrors,
}: {
  networkError?: Error;
  graphQLErrors?: readonly GraphQLError[];
}) {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      const error =
        `[GraphQL error]: Message: ${message}, Location: ` + `${locations}, Path: ${path}`;
      logger.error(new Error(error));
    });
  }
  if (networkError) {
    logger.error(networkError);
  }
}

function createApolloClient() {
  // Hugely inspired by apollo-link-error, but simplified and requires much fewer dependencies
  const errorLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let handle: Subscription | undefined;
        try {
          handle = forward(operation).subscribe({
            next: (result) => {
              if (result.errors) {
                errorHandler({ graphQLErrors: result.errors });
              }
              observer.next(result);
            },
            error: (networkError) => {
              errorHandler({
                networkError,
                graphQLErrors: networkError?.result?.errors,
              });
              observer.error(networkError);
            },
            complete: observer.complete.bind(observer),
          });
        } catch (e) {
          const networkError = ensureError(e);
          errorHandler({ networkError });
          observer.error(networkError);
        }

        return () => {
          handle?.unsubscribe();
        };
      }),
  );

  const link = ApolloLink.from([
    errorLink,
    new HttpLink({
      // If we receive "SSR" we know it's due to logging within the server
      headers: isServerSide ? { "x-device-id": "SSR" } : { "x-device-id": getLocalUniqueId() },
      uri: isServerSide ? getServerRuntimeConfig().serverGraphUrl : getClientSideGraphqlPath(),
    }),
  ]);

  return new ApolloClient({
    ssrMode: isServerSide, // set to true for SSR
    link,
    cache: new InMemoryCache({
      typePolicies: {
        Industry: { keyFields: ["slug"] },
        Topic: { keyFields: ["slug"] },
        Procedure: { keyFields: ["uid"] },
      },
    }),
  });
}

export function initializeApollo(initialState: Record<string, unknown> | null = null) {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Restore the cache using the data passed from
    // getStaticProps/getServerSideProps combined with the existing cached data
    _apolloClient.cache.restore({ ...existingCache, ...initialState });
  }

  // For SSG and SSR always create a new Apollo Client
  if (isServerSide) {
    return _apolloClient;
  }

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = _apolloClient;
  }
  return _apolloClient;
}

export function useApollo(initialState: Record<string, unknown> | undefined) {
  return useMemo(() => initializeApollo(initialState), [initialState]);
}
